import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, CircleAlert, X, Target, Sparkles, ChevronRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api_getSession, api_submitFeedback, api_getMyProfile } from "@/lib/api";

const OUTCOMES = [
  { key: "success", label: "Success", icon: Check, cls: "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200" },
  { key: "mixed", label: "Mixed", icon: CircleAlert, cls: "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200" },
  { key: "failed", label: "Failed", icon: X, cls: "bg-red-100 text-red-700 border-red-200 hover:bg-red-200" },
];

export default function SessionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [outcomes, setOutcomes] = useState({}); // drill_id -> [key,...]
  const [notes, setNotes] = useState({});
  const [activeDrill, setActiveDrill] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await api_getSession(id);
        setSession(s);
        const init = {};
        s.drills.forEach((d) => (init[d.id] = []));
        setOutcomes(init);
        const p = await api_getMyProfile();
        setProfile(p);
        if (s.status === "completed") navigate(`/reflection/${id}`);
      } catch (e) {
        toast.error("Could not load session.");
      }
    })();
  }, [id, navigate]);

  const tap = (drillId, outcome, shotCount) => {
    setOutcomes((prev) => {
      const cur = prev[drillId] || [];
      if (cur.length >= shotCount) {
        toast.info("You've logged all shots for this drill. Tap undo if needed.");
        return prev;
      }
      return { ...prev, [drillId]: [...cur, outcome] };
    });
  };

  const undo = (drillId) => {
    setOutcomes((prev) => ({ ...prev, [drillId]: (prev[drillId] || []).slice(0, -1) }));
  };

  const totalLogged = session ? session.drills.reduce((s, d) => s + (outcomes[d.id]?.length || 0), 0) : 0;
  const totalExpected = session ? session.drills.reduce((s, d) => s + d.shot_count, 0) : 0;

  const submit = async () => {
    if (totalLogged === 0) {
      toast.warning("Log at least a few shots before finishing.");
      return;
    }
    setSubmitting(true);
    try {
      const feedbacks = session.drills.map((d) => ({
        drill_id: d.id,
        outcomes: outcomes[d.id] || [],
        note: notes[d.id] || "",
      }));
      await api_submitFeedback({ session_id: id, feedbacks });
      toast.success("Session complete. Let's look at the data.");
      navigate(`/reflection/${id}`);
    } catch (e) {
      toast.error("Could not save session.");
      setSubmitting(false);
    }
  };

  if (!session) {
    return <AppShell profile={profile}><div className="text-center py-20 text-slate-500">Loading session…</div></AppShell>;
  }

  const d = session.drills[activeDrill];
  const logged = outcomes[d.id] || [];
  const s = logged.filter((o) => o === "success").length;
  const m = logged.filter((o) => o === "mixed").length;
  const f = logged.filter((o) => o === "failed").length;

  return (
    <AppShell profile={profile}>
      <button onClick={() => navigate("/home")} className="text-sm font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="session-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Header */}
      <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-8 mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">Today's Session · {session.time_minutes} min</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-black tracking-tight leading-tight" data-testid="session-title">{session.title}</h1>
        <p className="mt-3 text-white/70 leading-relaxed max-w-2xl">{session.summary}</p>
        <div className="mt-5 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">Progress</div>
          <div className="font-heading font-black text-lg tabular-nums" data-testid="session-progress">{totalLogged} / {totalExpected} shots</div>
        </div>
        <div className="mt-2 h-2 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#0B2545] transition-all duration-500" style={{ width: `${totalExpected ? (totalLogged / totalExpected) * 100 : 0}%` }} />
        </div>
      </div>

      {/* Drill tabs */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-2 -mx-1 px-1">
        {session.drills.map((dr, i) => {
          const drLogged = (outcomes[dr.id] || []).length;
          const pct = dr.shot_count ? Math.round((drLogged / dr.shot_count) * 100) : 0;
          return (
            <button
              key={dr.id}
              data-testid={`drill-tab-${i}`}
              onClick={() => setActiveDrill(i)}
              className={`shrink-0 px-4 py-3 rounded-2xl border-2 text-left min-w-[180px] transition-all ${activeDrill === i ? "border-[#0B2545] bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Drill {i + 1}</p>
              <p className="font-heading font-bold text-base leading-tight line-clamp-1 mt-1">{dr.title}</p>
              <div className="mt-2 h-1 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-[#0B2545]" style={{ width: `${pct}%` }} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Active drill */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="chip text-[#0B2545] border-[#0B2545]/30 bg-blue-50">{d.skill.replace("_", " ")}</span>
          <span className="chip">{d.duration_minutes} min</span>
          <span className="chip">{d.shot_count} shots</span>
        </div>
        <h2 className="mt-4 text-3xl font-heading font-black tracking-tight" data-testid="drill-title">{d.title}</h2>
        <p className="mt-3 text-slate-700 leading-relaxed">{d.instructions}</p>

        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500"><Target className="w-3 h-3 inline mr-1" /> Success criteria</p>
            <p className="mt-2 font-semibold text-slate-900">{d.success_criteria}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500"><Sparkles className="w-3 h-3 inline mr-1" /> Focus cue</p>
            <p className="mt-2 font-semibold text-slate-900">{d.focus_cue}</p>
          </div>
        </div>

        {/* Tap interface */}
        <div className="mt-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Tap after each shot</p>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {OUTCOMES.map((o) => {
              const Icon = o.icon;
              return (
                <button
                  key={o.key}
                  data-testid={`tap-${o.key}`}
                  onClick={() => tap(d.id, o.key, d.shot_count)}
                  className={`py-6 rounded-2xl border-2 transition-all active:scale-[0.96] flex flex-col items-center gap-2 ${o.cls}`}
                >
                  <Icon className="w-6 h-6" />
                  <span className="font-bold text-sm">{o.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="flex gap-3 text-sm font-bold">
              <span className="text-emerald-700">✓ {s}</span>
              <span className="text-amber-700">~ {m}</span>
              <span className="text-red-700">✗ {f}</span>
            </div>
            <button
              onClick={() => undo(d.id)}
              disabled={!logged.length}
              data-testid="tap-undo"
              className="text-sm font-bold text-slate-500 hover:text-slate-900 disabled:opacity-40"
            >
              Undo last
            </button>
          </div>
          {/* shot dots */}
          <div className="mt-4 flex flex-wrap gap-1.5">
            {Array.from({ length: d.shot_count }).map((_, idx) => {
              const o = logged[idx];
              const bg = !o ? "bg-slate-200" : o === "success" ? "bg-emerald-500" : o === "mixed" ? "bg-amber-500" : "bg-blue-500";
              return <div key={idx} data-testid={`shot-dot-${idx}`} className={`w-3 h-3 rounded-full ${bg}`} />;
            })}
          </div>
        </div>

        {/* Note */}
        <div className="mt-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Optional note</p>
          <textarea
            data-testid="drill-note"
            value={notes[d.id] || ""}
            onChange={(e) => setNotes({ ...notes, [d.id]: e.target.value })}
            placeholder="e.g. 'thin contact', 'missed right', 'better tempo'…"
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium focus:border-[#0B2545] focus:ring-2 focus:ring-[#0B2545]/20 outline-none min-h-[80px]"
          />
        </div>
      </div>

      {/* Footer controls */}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={() => setActiveDrill((i) => Math.max(0, i - 1))}
          data-testid="drill-prev"
          disabled={activeDrill === 0}
          className="btn-ghost disabled:opacity-40"
        >
          <ArrowLeft className="w-4 h-4" /> Prev drill
        </button>
        {activeDrill < session.drills.length - 1 ? (
          <button onClick={() => setActiveDrill((i) => i + 1)} data-testid="drill-next" className="btn-primary">
            Next drill <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} data-testid="session-finish" disabled={submitting} className="btn-primary disabled:opacity-60">
            {submitting ? "Finishing…" : "Finish session"} <Check className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Exit to home */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={() => navigate("/")}
          data-testid="session-back-home"
          className="btn-ghost text-sm py-3 px-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>
      </div>
    </AppShell>
  );
}
